import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import styled from 'styled-components';

const PageWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: 1fr 400px;
    gap: 2rem;
    align-items: start;
  }
`;

const ComparisonSection = styled.section`
  margin-bottom: 2rem;
  @media (max-width: 600px) {
    margin-bottom: 1.25rem;
  }
  @media (min-width: 768px) {
    margin-bottom: 0;
  }
`;

const ComparisonTable = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.cardBg};
  transition: border-color 0.3s ease, background 0.3s ease;
  box-shadow: ${props => props.theme.shadow || '0 2px 8px rgba(0, 0, 0, 0.06)'};
`;

const ComparisonCardIntro = styled.div`
  padding: 1rem 1rem 0.85rem;
  border-bottom: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.secondaryBg || 'rgba(0, 0, 0, 0.04)'};
  transition: border-color 0.3s ease, background 0.3s ease;
  @media (max-width: 600px) {
    padding: 0.85rem 0.75rem 0.7rem;
  }
`;

const ComparisonDateLine = styled.p`
  margin: 0 0 0.5rem;
  font-size: 0.8rem;
  color: ${props => props.theme.secondaryText};
  font-weight: 500;
  letter-spacing: 0.02em;
  transition: color 0.3s ease;
`;

const ComparisonCardTitle = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  color: ${props => props.theme.primaryText};
  font-weight: 700;
  line-height: 1.35;
  transition: color 0.3s ease;
  @media (max-width: 600px) {
    font-size: 1.05rem;
  }
`;

const ComparisonGroupBanner = styled.div`
  padding: 0.5rem 0.75rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${props => props.theme.secondaryText};
  background: ${props => props.theme.secondaryBg || 'rgba(0, 0, 0, 0.05)'};
  border-bottom: 1px solid ${props => props.theme.border};
  transition: color 0.3s ease, border-color 0.3s ease, background 0.3s ease;
  @media (min-width: 480px) {
    padding: 0.55rem 1rem;
    font-size: 0.75rem;
  }
`;

const ComparisonRow = styled.div`
  display: grid;
  /* Wider tier columns so “Guest” / “Free account” fit on one line; feature column uses remaining space */
  grid-template-columns: minmax(0, 1fr) minmax(4.5rem, auto) minmax(6.75rem, auto);
  align-items: center;
  gap: 0.35rem 0.5rem;
  padding: 0.55rem 0.75rem;
  font-size: 0.9rem;
  color: ${props => props.theme.secondaryText};
  border-bottom: 1px solid ${props => props.theme.border};
  transition: color 0.3s ease, border-color 0.3s ease, background 0.3s ease;
  ${(p) =>
    p.$alt &&
    `
    background: ${p.theme.secondaryBg || 'rgba(0, 0, 0, 0.035)'};
  `}
  &:last-of-type {
    border-bottom: none;
  }
  @media (min-width: 480px) {
    grid-template-columns: minmax(0, 1fr) minmax(5rem, auto) minmax(7.25rem, auto);
    padding: 0.6rem 1rem;
  }
  @media (max-width: 600px) {
    font-size: 0.8rem;
    padding: 0.45rem 0.5rem;
  }
`;

const ComparisonHeaderRow = styled(ComparisonRow)`
  font-weight: 600;
  font-size: 0.85rem;
  color: ${props => props.theme.primaryText};
  padding-top: 0.75rem;
  padding-bottom: 0.65rem;
  border-bottom: 2px solid ${props => props.theme.border};
  @media (min-width: 480px) {
    font-size: 0.95rem;
  }
  @media (max-width: 600px) {
    font-size: 0.78rem;
  }
`;

const ComparisonFeatureLabel = styled.span`
  line-height: 1.45;
  padding-right: 0.25rem;
`;

const ComparisonFeatureColumnHeader = styled(ComparisonFeatureLabel)`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${props => props.theme.secondaryText};
  align-self: end;
  padding-bottom: 0.15rem;
`;

const ComparisonIconCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: stretch;
  min-width: 1.5rem;
`;

/** Centered label over Guest / Free account columns (single line when width allows). */
const TierHeaderText = styled.span`
  display: block;
  width: 100%;
  text-align: center;
  white-space: nowrap;
  @media (max-width: 360px) {
    white-space: normal;
    word-break: break-word;
  }
`;

const ComparisonHeaderIconCell = styled(ComparisonIconCell)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Check = styled.span`
  color: #48bb78;
  flex-shrink: 0;
`;

const XMark = styled.span`
  color: ${props => props.theme.error || '#e53e3e'};
  flex-shrink: 0;
`;

const FormContainer = styled.div`
  max-width: 400px;
  margin: 0 auto;
  padding: 1.5rem;
  border-radius: 0.5rem;
  box-shadow: ${props => props.theme.shadow};
  background-color: ${props => props.theme.cardBg};
  transition: all 0.3s ease;
  @media (min-width: 768px) {
    margin: 0;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormTitle = styled.h2`
  text-align: center;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.primaryText};
  transition: color 0.3s ease;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: ${props => props.theme.primaryText};
  transition: color 0.3s ease;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 0.25rem;
  font-size: 1rem;
  background-color: ${props => props.theme.inputBg};
  color: ${props => props.theme.primaryText};
  transition: all 0.3s ease;
  
  &:focus {
    border-color: ${props => props.theme.inputFocusBorder};
    outline: none;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
  }
`;

const Button = styled.button`
  padding: 0.75rem;
  background-color: #4299e1;
  color: white;
  border: none;
  border-radius: 0.25rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  margin-top: 0.5rem;
  
  &:hover {
    background-color: #3182ce;
  }
  
  &:disabled {
    background-color: #a0aec0;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.error};
  margin-top: 1rem;
  text-align: center;
  font-size: 0.875rem;
  transition: color 0.3s ease;
`;

const LoginLink = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  font-size: 0.875rem;
  
  a {
    color: ${props => props.theme.primary};
    text-decoration: none;
    font-weight: 500;
    transition: color 0.3s ease;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const RegisterForm = () => {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { register, error } = useAuth();
  const navigate = useNavigate();

  const comparisonDateLabel = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }).format(new Date()),
    [],
  );

  const validateForm = () => {
    const errors = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      
      const success = await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password
      });
      
      if (success) {
        navigate('/dashboard');
      }
      
      setIsSubmitting(false);
    }
  };
  
  // Matches actual app behavior: prediction pages & Game Picks are public; odds/edge, SHAP, tracked picks, etc. need login
  const comparisonFeatures = [
    { label: 'View predictions (TB, HR, Hit, 2+ Hit)', guest: true, member: true },
    { label: 'Game Picks', guest: true, member: true },
    { label: 'Picks of the Day', guest: true, member: true },
    { label: 'Parlay of the Day', guest: true, member: true },
    { label: 'Model performance metrics', guest: true, member: true },
    { label: 'Betting calculators', guest: true, member: true },
    { label: 'Sportsbook odds', guest: false, member: true },
    { label: 'Calculated edge', guest: false, member: true },
    { label: 'Model features & insights', guest: false, member: true },
    { label: 'Early access to new models', guest: false, member: true },
    { label: 'Track/simulate your picks', guest: false, member: true },
    {
      label: 'Pick of the Day email notifications',
      guest: false,
      member: true,
    },
  ];

  const guestIncludedFeatures = comparisonFeatures.filter((f) => f.guest);
  const accountAddsFeatures = comparisonFeatures.filter((f) => !f.guest);

  return (
    <PageWrapper>
      <ComparisonSection>
        <ComparisonTable theme={theme} role="table" aria-label="Guest vs free account features">
          <ComparisonCardIntro theme={theme}>
            <ComparisonDateLine theme={theme}>{comparisonDateLabel}</ComparisonDateLine>
            <ComparisonCardTitle theme={theme}>
              Why create a free account?
            </ComparisonCardTitle>
          </ComparisonCardIntro>
          <ComparisonHeaderRow theme={theme} role="row">
            <ComparisonFeatureColumnHeader theme={theme} role="columnheader">
              Feature
            </ComparisonFeatureColumnHeader>
            <ComparisonHeaderIconCell theme={theme} role="columnheader">
              <TierHeaderText>Guest</TierHeaderText>
            </ComparisonHeaderIconCell>
            <ComparisonHeaderIconCell theme={theme} role="columnheader">
              <TierHeaderText>Free account</TierHeaderText>
            </ComparisonHeaderIconCell>
          </ComparisonHeaderRow>
          <ComparisonGroupBanner theme={theme} aria-hidden="true">
            No sign-in required
          </ComparisonGroupBanner>
          {guestIncludedFeatures.map((item, i) => (
            <ComparisonRow key={item.label} theme={theme} role="row" $alt={i % 2 === 1}>
              <ComparisonFeatureLabel theme={theme} role="rowheader">
                {item.label}
              </ComparisonFeatureLabel>
              <ComparisonIconCell
                theme={theme}
                role="cell"
                aria-label={item.guest ? 'Included for guests' : 'Not included for guests'}
              >
                {item.guest ? <Check>✓</Check> : <XMark theme={theme}>✕</XMark>}
              </ComparisonIconCell>
              <ComparisonIconCell
                theme={theme}
                role="cell"
                aria-label={item.member ? 'Included with free account' : 'Not included with free account'}
              >
                {item.member ? <Check>✓</Check> : <XMark theme={theme}>✕</XMark>}
              </ComparisonIconCell>
            </ComparisonRow>
          ))}
          <ComparisonGroupBanner theme={theme} aria-hidden="true">
            Included with a free account
          </ComparisonGroupBanner>
          {accountAddsFeatures.map((item, i) => (
            <ComparisonRow key={item.label} theme={theme} role="row" $alt={i % 2 === 1}>
              <ComparisonFeatureLabel theme={theme} role="rowheader">
                {item.label}
              </ComparisonFeatureLabel>
              <ComparisonIconCell
                theme={theme}
                role="cell"
                aria-label={item.guest ? 'Included for guests' : 'Not included for guests'}
              >
                {item.guest ? <Check>✓</Check> : <XMark theme={theme}>✕</XMark>}
              </ComparisonIconCell>
              <ComparisonIconCell
                theme={theme}
                role="cell"
                aria-label={item.member ? 'Included with free account' : 'Not included with free account'}
              >
                {item.member ? <Check>✓</Check> : <XMark theme={theme}>✕</XMark>}
              </ComparisonIconCell>
            </ComparisonRow>
          ))}
        </ComparisonTable>
      </ComparisonSection>

      <FormContainer theme={theme}>
        <FormTitle theme={theme}>Create an Account</FormTitle>

        <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label theme={theme} htmlFor="firstName">First Name</Label>
          <Input
            theme={theme}
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
          />
          {formErrors.firstName && <ErrorMessage theme={theme}>{formErrors.firstName}</ErrorMessage>}
        </FormGroup>
        
        <FormGroup>
          <Label theme={theme} htmlFor="lastName">Last Name</Label>
          <Input
            theme={theme}
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
          />
        </FormGroup>
        
        <FormGroup>
          <Label theme={theme} htmlFor="email">Email</Label>
          <Input
            theme={theme}
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
          />
          {formErrors.email && <ErrorMessage theme={theme}>{formErrors.email}</ErrorMessage>}
        </FormGroup>
        
        <FormGroup>
          <Label theme={theme} htmlFor="password">Password</Label>
          <Input
            theme={theme}
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
          />
          {formErrors.password && <ErrorMessage theme={theme}>{formErrors.password}</ErrorMessage>}
        </FormGroup>
        
        <FormGroup>
          <Label theme={theme} htmlFor="confirmPassword">Confirm Password</Label>
          <Input
            theme={theme}
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
          />
          {formErrors.confirmPassword && <ErrorMessage theme={theme}>{formErrors.confirmPassword}</ErrorMessage>}
        </FormGroup>
        
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Creating Account...' : 'Sign Up'}
        </Button>
        
        {error && <ErrorMessage theme={theme}>{error}</ErrorMessage>}
        </Form>

        <LoginLink theme={theme}>
          Already have an account? <Link to="/login">Log in</Link>
        </LoginLink>
      </FormContainer>
    </PageWrapper>
  );
};

export default RegisterForm; 